// The shared downloads block: the platform-detected primary download (Windows
// for the Windows testers), the three desktop platforms as ruled coupon cells
// (Windows listed first), the unsigned first-run steps (SmartScreen leads), and
// the mobile links. Rendered inside the newspaper-receipt idiom (the landing /
// sign-in design language). Token-pure; square / flat / EXACTLY one amber per
// render — the stamp edge on the live primary download, or the pending note
// when no installer URL is configured (never both).

import { Download } from "lucide-react";
import { Eyebrow } from "../receipt/ReceiptKit";
import {
  DEFAULT_INSTALLERS,
  MOBILE_APPS,
  platformLabel,
  readUserAgent,
  selectPreferredInstaller,
  type DesktopInstallerOption,
  type InstallerId,
} from "./installers";

type Props = {
  installers?: readonly DesktopInstallerOption[];
  userAgent?: string;
  /**
   * "full" (default) renders the primary download + every installer + the
   * first-run help + mobile — the single source of truth, used once per page.
   * "compact" renders only the platform-detected primary + a quiet
   * "already installed" link + an anchor to the full block.
   */
  variant?: "full" | "compact";
  /** Anchor id the compact "see every download" link points at (the full block). */
  fullBlockHref?: string;
};

// The display order for the desktop cells — Windows first (the testers are on
// Windows), then macOS, then Linux. Detection still picks the visitor's own
// platform for the primary CTA; this only orders the printed list.
const DISPLAY_ORDER: Record<InstallerId, number> = {
  windows: 0,
  macos: 1,
  linux: 2,
};

export function PlatformDownloads({
  installers = DEFAULT_INSTALLERS,
  userAgent = readUserAgent(),
  variant = "full",
  fullBlockHref = "#get",
}: Props = {}) {
  const preferred = selectPreferredInstaller(installers, userAgent);
  const compact = variant === "compact";
  const ordered = [...installers].sort(
    (a, b) => (DISPLAY_ORDER[a.id] ?? 9) - (DISPLAY_ORDER[b.id] ?? 9),
  );

  // The primary download (or the disabled "pending" placeholder) + the quiet
  // "already installed?" handoff. When the download is live the row carries the
  // amber stamp edge (the steer-card "needs you" idiom) — the render's one amber.
  const primaryRow = (
    <div
      className={
        preferred ? "wt-dl__primary wt-dl__primary--live" : "wt-dl__primary"
      }
    >
      <div className="wt-receipt-cta">
        {preferred ? (
          <a
            href={preferred.href}
            className="wt-receipt-button wt-receipt-button--lg"
            data-testid="desktop-download-primary"
          >
            <Download size={16} aria-hidden />
            <span>Download for {platformLabel(preferred.id)}</span>
          </a>
        ) : (
          <button
            type="button"
            disabled
            aria-describedby="installer-pending-note"
            className="wt-receipt-button wt-receipt-button--lg wt-dl__primary-btn"
            data-testid="desktop-download-primary"
          >
            <Download size={16} aria-hidden />
            <span>Installer pending</span>
          </button>
        )}
        <a
          href="waytide://orchestrator"
          className="wt-receipt-link"
          data-testid="desktop-open-installed"
        >
          already installed? open waytide
        </a>
      </div>
      {preferred && !compact ? (
        <span className="wt-dl__primary-meta">
          {preferred.detail} · unsigned preview — first run steps below
        </span>
      ) : null}
    </div>
  );

  if (compact) {
    return (
      <div className="wt-dl">
        {primaryRow}
        <a href={fullBlockHref} className="wt-receipt-link">
          <span>every platform &amp; install steps</span>
          <span aria-hidden>&darr;</span>
        </a>
        {!preferred && (
          <p id="installer-pending-note" className="wt-dl__note">
            No public installer URL is configured for this environment yet. The
            app link is only useful after the desktop app is installed.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="wt-dl">
      {primaryRow}

      {/* Every desktop platform — three ruled coupon cells, Windows first. The
          visitor's own platform carries the inverted "detected" chit. */}
      <div className="wt-dl__group">
        <Eyebrow label="EVERY DESKTOP" />
        <div className="wt-dl__grid">
          {ordered.map((installer) => {
            const detected = preferred?.id === installer.id;
            return (
              <div key={installer.id} className="wt-dl__cell">
                <p className="wt-dl__cell-head">
                  <span className="wt-dl__cell-name">{installer.label}</span>
                  {detected ? (
                    <span className="wt-dl__cell-chit">detected</span>
                  ) : null}
                </p>
                <p className="wt-dl__cell-detail">{installer.detail}</p>
                {installer.href ? (
                  <a href={installer.href} className="wt-receipt-link">
                    <span>download</span>
                    <span aria-hidden>&darr;</span>
                  </a>
                ) : (
                  <span className="wt-dl__pending">pending</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* First run (unsigned) — SmartScreen first; the testers are on Windows. */}
      <div className="wt-dl__group" data-testid="first-run-help">
        <Eyebrow label="FIRST RUN" />
        <p className="wt-dl__hint">
          The builds aren&apos;t code-signed yet, so your OS warns once on first
          launch. They&apos;re safe &mdash; clear the warning like this:
        </p>
        <ul className="wt-dl__steps">
          <li className="wt-dl__step">
            <span className="wt-dl__os">Windows</span>
            <span className="wt-dl__how">
              SmartScreen says &ldquo;Windows protected your PC&rdquo; &mdash;
              choose <strong>More info</strong>, then <strong>Run anyway</strong>.
            </span>
          </li>
          <li className="wt-dl__step">
            <span className="wt-dl__os">macOS</span>
            <span className="wt-dl__how">
              If it says &ldquo;damaged&rdquo;, run this once in Terminal, then
              open it (or right-click &rarr; Open &rarr; Open):
              <code className="wt-dl__code">
                xattr -dr com.apple.quarantine &quot;/Applications/Waytide.app&quot;
              </code>
            </span>
          </li>
          <li className="wt-dl__step">
            <span className="wt-dl__os">Linux</span>
            <span className="wt-dl__how">
              Make the AppImage executable, then run it:
              <code className="wt-dl__code">
                chmod +x Waytide*.AppImage &amp;&amp; ./Waytide*.AppImage
              </code>
            </span>
          </li>
        </ul>
      </div>

      {/* Mobile (secondary) — quiet ruled rows. */}
      <div className="wt-dl__group">
        <Eyebrow label="ON YOUR PHONE" />
        <div className="wt-dl__list">
          {MOBILE_APPS.map((app) => (
            <div key={app.id} className="wt-dl__row">
              <div>
                <p className="wt-dl__row-label">{app.label}</p>
                <p className="wt-dl__row-detail">{app.detail}</p>
              </div>
              {app.href ? (
                <a href={app.href} className="wt-receipt-link">
                  <span>get</span>
                  <span aria-hidden>&rarr;</span>
                </a>
              ) : (
                <span className="wt-dl__pending">coming soon</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {!preferred && (
        <p id="installer-pending-note" className="wt-dl__note">
          No public installer URL is configured for this environment yet. The app
          link is only useful after the desktop app is installed.
        </p>
      )}
    </div>
  );
}
